import type { FlowGraph, Json, PresencePeer, RunLog, StepTrace } from '@forge/shared';
import { io, type Socket } from 'socket.io-client';
import { getAccessToken } from './api';

export interface EntityChanged {
  projectId: string;
  entity: 'endpoint' | 'dashboard' | 'datasource' | 'token' | 'member' | 'project';
  id: string;
  action: 'created' | 'updated' | 'deleted';
  actorId: string;
}

export interface GraphPatch {
  endpointId: string;
  graph: FlowGraph;
  version: number;
  by: string;
}

export interface GraphPreview {
  endpointId: string;
  graph: FlowGraph;
  by: { id: string; name: string; color: string };
}

export interface RunStep {
  projectId: string;
  endpointId: string;
  runId: string;
  step: StepTrace;
}

export interface ServerToClient {
  presence: (payload: { room: string; peers: PresencePeer[] }) => void;
  'entity:changed': (payload: EntityChanged) => void;
  'graph:patch': (payload: GraphPatch) => void;
  'graph:preview': (payload: GraphPreview) => void;
  'run:log': (payload: RunLog) => void;
  'run:step': (payload: RunStep) => void;
  'dashboard:invalidate': (payload: { dashboardId: string; reason: string }) => void;
  'stream:messages': (payload: { channel: string; messages: Json[]; at: string }) => void;
}

let socket: Socket | null = null;

/**
 * One shared connection for the whole app. Rooms are joined per view, so a
 * builder tab and a dashboard tab in the same window reuse the same transport.
 */
export function getSocket(): Socket {
  if (socket) return socket;

  socket = io({
    path: '/realtime',
    transports: ['websocket', 'polling'],
    auth: (cb) => cb({ token: getAccessToken() }),
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
    withCredentials: true,
    autoConnect: true,
  });

  return socket;
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
}

export interface JoinResult {
  ok: boolean;
  error?: string;
  peers?: PresencePeer[];
  socketId?: string;
}

export function joinRoom(room: string): Promise<JoinResult> {
  return new Promise((resolve) => {
    const s = getSocket();
    const emit = () => s.emit('join', { room }, (result: JoinResult) => resolve(result));
    if (s.connected) emit();
    else s.once('connect', emit);
  });
}

export function leaveRoom(room: string): void {
  socket?.emit('leave', { room });
}

export function sendCursor(room: string, cursor: { x: number; y: number } | null, selection: string[]): void {
  socket?.emit('cursor', { room, cursor, selection });
}

export function sendGraphPreview(endpointId: string, graph: FlowGraph): void {
  socket?.emit('graph:preview', { endpointId, graph });
}
